import * as Dialog from '@radix-ui/react-dialog';
import { RxCross2 } from 'react-icons/rx';
import { Separator } from '@radix-ui/react-separator';
import { DocumentService } from '../../../../../services/DocumentService';
import { ManagementService } from '../../../../../services/ManagementService';
import { useLocalStorage } from '../../../../../hooks/useLocalStorage';
import { useDialogReproveDocumentStore } from '../../store/dialogs.store';
import { SubmitHandler, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { errorToast, successToast } from '../../../../../utils/toasts';
import { errorMessageAdapter } from '../../../../../adapters';
import { AxiosError, AxiosInstance } from 'axios';
import { useState } from 'react';
import { useApi } from '../../../../../services/hooks/apiHook';

interface IRejected {
  reason: string;
  complementary: string;
}

const DialogRejected = ({ shareholderSlug, documentSlug, onDocumentApproved, onClose }: any) => {
  const { open: openRejected, setOpen: setOpenRejected }: any = useDialogReproveDocumentStore();
  const api: AxiosInstance = useApi();
  const { t, i18n } = useTranslation();
  const [token] = useLocalStorage('access-token', null);
  const [loading, setLoading] = useState(false);
  const validationSchema = Yup.object().shape({
    reason: Yup.string().required('Required'),
    complementary: Yup.string().required('Required')
  });
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<IRejected>({
    resolver: yupResolver(validationSchema)
  });
  const reproveDocument: SubmitHandler<IRejected> = async (data: IRejected) => {
    try {
      setLoading(true);

      await DocumentService.reproveDocument(
        api,
        {
          shareholder_slug: shareholderSlug,
          document_slug: documentSlug,
          subject: data.reason,
          message: data.complementary
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Language': i18n.language.toLowerCase()
          }
        }
      );

      try {
        await ManagementService.saveApprovedOrRejectDocument(
          api,
          documentSlug,
          {
            action: 'rejected'
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Language': i18n.language.toLowerCase()
            }
          }
        );
      } catch (secondError) {
        console.log('Segunda tentativa falhou, mas primeira pode ter funcionado:', secondError);
      }

      successToast(t('others.othDocumentReproved') || 'Documento rejeitado com sucesso!');

      setLoading(false);
      setOpenRejected(false);
      onDocumentApproved('rejected');
      if (onClose) {
        onClose();
      }
      reset();
    } catch (e) {
      setLoading(false);
      errorToast(errorMessageAdapter(e as AxiosError));
    }
  };

  return (
    <Dialog.Root open={openRejected} onOpenChange={setOpenRejected}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm data-[state=open]:animate-overlayShow" />
        <Dialog.Content className="fixed left-[50%] top-[50%] max-h-[90vh] w-[95%] max-w-[500px] translate-x-[-50%] translate-y-[-50%] rounded-xl bg-white shadow-2xl focus:outline-none data-[state=open]:animate-contentShow overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-red-50 to-rose-50 px-6 py-4 border-b border-red-200">
            <Dialog.Title className="text-xl font-semibold text-gray-800 flex items-center gap-2">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              {t('others.othModalRejected')}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                className="absolute right-4 top-4 p-1 hover:bg-red-100 rounded-lg transition-colors"
                aria-label="Close"
              >
                <RxCross2 className="w-5 h-5 text-gray-500" />
              </button>
            </Dialog.Close>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit(reproveDocument)} className="flex flex-col h-full">
            <div className="p-6 space-y-6 flex-1">
              {/* Icon and description */}
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.232 15.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <p className="text-gray-600 text-sm">
                  {t('others.othPleaseProvideReason') || 'Por favor, forneça o motivo da rejeição'}
                </p>
              </div>

              {/* Reason for rejection */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  {t('others.othReasonForRejection') || 'Motivo da rejeição'}
                </label>
                <input
                  {...register('reason')}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-colors ${
                    errors.reason ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder={t('others.othEnterReason') || 'Digite o motivo...'}
                />
                {errors.reason && (
                  <p className="text-sm text-red-600 flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {errors.reason.message}
                  </p>
                )}
              </div>

              {/* Complementary message */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  {t('others.othComplementaryMessage') || 'Mensagem complementar'}
                </label>
                <textarea
                  {...register('complementary')}
                  rows={4}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-colors resize-none ${
                    errors.complementary ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder={t('others.othEnterAdditionalDetails') || 'Digite detalhes adicionais...'}
                />
                {errors.complementary && (
                  <p className="text-sm text-red-600 flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {errors.complementary.message}
                  </p>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
              <div className="flex justify-end gap-3">
                <Dialog.Close asChild>
                  <button
                    type="button"
                    className="flex items-center justify-center px-6 py-3 bg-white hover:bg-gray-50 border border-gray-300 rounded-lg text-gray-700 font-medium transition-colors"
                    disabled={loading}
                  >
                    {t('others.othCancel')}
                  </button>
                </Dialog.Close>
                
                <button
                  type="submit"
                  disabled={loading}
                  className="flex items-center justify-center px-6 py-3 bg-red-600 hover:bg-red-700 rounded-lg text-white font-medium transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <div className="w-5 h-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  ) : (
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      <span>{t('others.othRejectDocument') || 'Rejeitar'}</span>
                    </div>
                  )}
                </button>
              </div>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default DialogRejected;
